"use client";

import React from "react";
import { Wrench, ShieldCheck, Clock, FileCheck, ArrowRight, Settings, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const serviceHighlights = [{
  id: "preventive",
  title: "Scheduled Maintenance",
  description: "Routine inspections, cleaning, alignment, and optical checks to eliminate unplanned downtime.",
  icon: Clock,
  badge: "Planned Uptime",
  points: ["Calibration verification", "Optical filter inspection", "Firmware updates"]
}, {
  id: "repairs",
  title: "Emergency & On-Site Repairs",
  description: "Rapid diagnostic and component replacement services performed by certified service specialists.",
  icon: Wrench,
  badge: "Fast Response",
  points: ["OEM replacement parts", "Full diagnostic scan", "Recalibration on completion"]
}, {
  id: "compliance",
  title: "Qualification & Compliance",
  description: "Complete IQ/OQ/PQ support and traceable documentation for regulated pharmaceutical facilities.",
  icon: ShieldCheck,
  badge: "Regulated Ready",
  points: ["Traceable standards", "21 CFR Part 11 checks", "Audit-ready reports"]
}];
export default function Maintenance() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="preventivemaintenanceandrepairs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border relative overflow-hidden">
      <div className="page-container">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Lifecycle Services
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Preventive Maintenance &amp; Laboratory Repairs
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Protect analytical accuracy and extend instrument lifespan. Antech Solutions provides
            comprehensive service plans, OEM diagnostics, and compliant on-site repair across Ireland and the UK.
          </p>
        </motion.div>

        {/* Feature Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {serviceHighlights.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} variants={itemVariants} data-index={index}>
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-12 rounded-lg bg-secondary text-primary flex items-center justify-center">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm mt-2 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <ul className="space-y-2 text-sm text-card-foreground">
                      {service.points.map((point, pointIndex) => <li key={pointIndex} className="flex items-center gap-2">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-4 border-t border-border">
                    <Button asChild variant="ghost" className="w-full justify-between px-0 hover:bg-transparent text-primary hover:text-primary/80">
                      <Link to="/PreventiveMaintenanceAndRepairsPage">
                        <span>Explore full service plan</span>
                        <ArrowRight className="size-4 ml-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* CTA Banner Strip */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="bg-secondary rounded-2xl p-6 md:p-8 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-1">
              <Settings className="size-5" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-secondary-foreground">
                Require urgent technical support or a custom maintenance contract?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our service engineers support Raman, NIR, and powder testing systems across all major manufacturers.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <Link to="/SupportRequestPage">
                Submit Support Request
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/PreventiveMaintenanceAndRepairsPage">
                View All Maintenance Plans
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}